import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    atualizarCadastro,
    listarPromocoesEstabelecimento,
    obterCadastro,
} from "../../../services/cadastroAplicativo"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { CadastroAplicativoInicial } from "../models/cadastroAplicativoModel"
import { CadastroAplicativoActionsTypes } from "./cadastroAplicativoActions"
import { CadastroAplicativoContext } from "./CadastroAplicativoContext"
import { cadastroAplicativoReducer } from "./cadastroAplicativoReducer"

export function CadastroAplicativoContextProvider({ children }: { children: React.ReactNode }) {
    const [dados, dispatch] = useReducer(cadastroAplicativoReducer, CadastroAplicativoInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await obterCadastro(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar dados do aplicativo")
            } else {
                setErroCarregamento(null)
                dispatch({
                    type: CadastroAplicativoActionsTypes.CARREGAR,
                    payload: {
                        ...CadastroAplicativoInicial,
                        ...response,
                        appNome: response.appNome ?? "",
                        urlAppAndroid: response.urlAppAndroid ?? "",
                        urlAppIphone: response.urlAppIphone ?? "",
                        codigoFireBase: response.codigoFireBase ?? "",
                        urlAcesso: response.urlAcesso ?? "",
                        urlAcesso2: response.urlAcesso2 ?? "",
                        urlAcesso3: response.urlAcesso3 ?? "",
                        urlArtesDivulgacao: response.urlArtesDivulgacao ?? "",
                        packageName: response.packageName ?? "",
                        categorias: response.categorias ?? "",
                        appNomeAlternativo: response.appNomeAlternativo ?? "",
                        dataPrevisaoLancamento: response.dataPrevisaoLancamento ?? "",
                        dataLancamento: response.dataLancamento ?? "",
                        ativo: response.ativo !== false,
                        podeEditar: response.podeEditar !== false,
                    },
                })
            }
        } catch {
            setErroCarregamento("Erro ao carregar dados do aplicativo")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onEstabelecimentoChange = useCallback(
        async (idEstabelecimento: number | null) => {
            dispatch({
                type: CadastroAplicativoActionsTypes.ATUALIZAR_CAMPO,
                payload: { campo: "idEstabelecimentoPrincipal", valor: idEstabelecimento },
            })
            if (idEstabelecimento == null) {
                dispatch({
                    type: CadastroAplicativoActionsTypes.CARREGAR_PROMOCOES,
                    payload: [],
                })
                return
            }
            const autorizacao = localStorage.getItem("chaveSistema")
            if (!autorizacao) return
            const res = await listarPromocoesEstabelecimento(autorizacao, idEstabelecimento)
            if (!("retornoErro" in res)) {
                dispatch({
                    type: CadastroAplicativoActionsTypes.CARREGAR_PROMOCOES,
                    payload: res,
                })
            }
        },
        [dispatch]
    )

    const salvar = useCallback(async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const payload = {
                urlAppAndroid: dados.urlAppAndroid || null,
                urlAppIphone: dados.urlAppIphone || null,
                codigoFireBase: dados.codigoFireBase || null,
                urlAcesso: dados.urlAcesso || null,
                urlAcesso2: dados.urlAcesso2 || null,
                urlAcesso3: dados.urlAcesso3 || null,
                urlArtesDivulgacao: dados.urlArtesDivulgacao || null,
                packageName: dados.packageName || null,
                categorias: dados.categorias || null,
                appNomeAlternativo: dados.appNomeAlternativo || null,
                situacao: dados.situacao,
                ativo: dados.ativo,
                idEstabelecimentoPrincipal: dados.idEstabelecimentoPrincipal,
                idPromocaoPrincipal: dados.idPromocaoPrincipal,
                dataPrevisaoLancamento: dados.dataPrevisaoLancamento || null,
                dataLancamento: dados.dataLancamento || null,
            }
            const response = await atualizarCadastro(autorizacao, payload)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({
                type: CadastroAplicativoActionsTypes.CARREGAR,
                payload: {
                    ...dados,
                    ...response,
                    appNome: response.appNome ?? dados.appNome,
                    urlAppAndroid: response.urlAppAndroid ?? "",
                    urlAppIphone: response.urlAppIphone ?? "",
                    codigoFireBase: response.codigoFireBase ?? "",
                    urlAcesso: response.urlAcesso ?? "",
                    urlAcesso2: response.urlAcesso2 ?? "",
                    urlAcesso3: response.urlAcesso3 ?? "",
                    urlArtesDivulgacao: response.urlArtesDivulgacao ?? "",
                    packageName: response.packageName ?? "",
                    categorias: response.categorias ?? "",
                    appNomeAlternativo: response.appNomeAlternativo ?? "",
                    dataPrevisaoLancamento: response.dataPrevisaoLancamento ?? "",
                    dataLancamento: response.dataLancamento ?? "",
                    ativo: response.ativo !== false,
                    podeEditar: response.podeEditar !== false,
                },
            })
        } finally {
            setLoading(false)
        }
    }, [dados, setLoading])

    return (
        <CadastroAplicativoContext.Provider
            value={{ dados, dispatch, salvar, onEstabelecimentoChange, erroCarregamento }}
        >
            <Loading />
            {children}
        </CadastroAplicativoContext.Provider>
    )
}
